import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Loader2,
  Save,
  Send,
  CheckCircle2,
  XCircle,
  CreditCard,
  Mail,
  Webhook,
  RefreshCw,
} from "lucide-react";

function StatusPill({ ok, label, testid }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
        ok
          ? "border-primary/40 text-primary bg-primary/5"
          : "border-amber-500/40 text-amber-300 bg-amber-500/5"
      }`}
      data-testid={testid}
    >
      {ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
      {label}
    </span>
  );
}

export default function AdminBookingSettings() {
  const [slots, setSlots] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Diagnostics
  const [stripeDiag, setStripeDiag] = useState(null);
  const [emailDiag, setEmailDiag] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState(null);
  const [refreshingDiag, setRefreshingDiag] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  const refreshDiagnostics = useCallback(async () => {
    setRefreshingDiag(true);
    try {
      const [s, e, w] = await Promise.all([
        api.get("/admin/stripe/diagnostic"),
        api.get("/admin/email/diagnostic"),
        api.get("/admin/stripe/webhook-events", { params: { limit: 20 } }),
      ]);
      setStripeDiag(s.data);
      setEmailDiag(e.data);
      setWebhookEvents(w.data);
    } catch (err) {
      // Diagnostic endpoints are superadmin-only; admins simply won't see them.
      if (err?.response?.status !== 403) toast.error(formatApiError(err));
    } finally {
      setRefreshingDiag(false);
    }
  }, []);

  useEffect(() => {
    api.get("/admin/booking/slots-config").then((r) => setSlots(r.data));
    api.get("/admin/booking/pricing").then((r) => setPricing(r.data));
    refreshDiagnostics();
  }, [refreshDiagnostics]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/booking/slots-config", {
        open_window_minutes: slots.open_window_minutes,
        slot_step_minutes: slots.slot_step_minutes,
        last_seating_offset_minutes: slots.last_seating_offset_minutes,
        max_party_size: slots.max_party_size,
      });
      await api.put("/admin/booking/pricing", {
        default_mode: pricing.default_mode,
        flat_fee_amount: pricing.flat_fee_amount,
        min_consumption_per_person: pricing.min_consumption_per_person,
        currency: pricing.currency || "EUR",
        deposit_required: pricing.deposit_required,
        reservation_only_enabled: pricing.reservation_only_enabled,
        pay_now_enabled: pricing.pay_now_enabled,
        allow_user_choice: pricing.allow_user_choice,
      });
      toast.success("Booking settings saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSendingTest(true);
    try {
      const { data } = await api.post("/admin/email/test", { to: testEmail });
      toast.success(`Test email ${data.status === "ok" ? "sent" : "failed"} (delivery: ${data.delivery})`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSendingTest(false);
    }
  };

  if (!slots || !pricing) {
    return (
      <AdminLayout>
        <div className="py-16 flex justify-center text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-10" data-testid="admin-booking-settings-page">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary/80">CMS</p>
            <h1 className="font-display text-4xl sm:text-5xl mt-2">Booking settings</h1>
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            data-testid="booking-settings-save"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Save className="w-4 h-4 mr-2" /> Save</>)}
          </Button>
        </div>

        <section className="border border-border rounded-xl bg-card p-6 space-y-4" data-testid="slots-card">
          <h2 className="font-display text-xl">Slots configuration</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="slots-duration">Booking duration (minutes)</Label>
              <Input id="slots-duration" type="number" value={slots.open_window_minutes} onChange={(e) => setSlots({ ...slots, open_window_minutes: parseInt(e.target.value || "0") })} data-testid="slots-duration" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slots-step">Step (minutes)</Label>
              <Input id="slots-step" type="number" value={slots.slot_step_minutes} onChange={(e) => setSlots({ ...slots, slot_step_minutes: parseInt(e.target.value || "0") })} data-testid="slots-step" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slots-last-offset">Last seating offset before close (minutes)</Label>
              <Input id="slots-last-offset" type="number" value={slots.last_seating_offset_minutes} onChange={(e) => setSlots({ ...slots, last_seating_offset_minutes: parseInt(e.target.value || "0") })} data-testid="slots-last-offset" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slots-max-party">Max party size</Label>
              <Input id="slots-max-party" type="number" value={slots.max_party_size} onChange={(e) => setSlots({ ...slots, max_party_size: parseInt(e.target.value || "0") })} data-testid="slots-max-party" />
            </div>
          </div>
        </section>

        <section className="border border-border rounded-xl bg-card p-6 space-y-4" data-testid="pricing-card">
          <h2 className="font-display text-xl">Pricing</h2>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="pricing-mode-trigger">Default mode</Label>
              <Select value={pricing.default_mode} onValueChange={(v) => setPricing({ ...pricing, default_mode: v })}>
                <SelectTrigger id="pricing-mode-trigger" data-testid="pricing-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="reservation_only">Reservation only</SelectItem>
                  <SelectItem value="flat_fee">Flat fee</SelectItem>
                  <SelectItem value="min_consumption">Minimum consumption</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pricing-flat-fee">Flat fee amount (EUR)</Label>
              <Input id="pricing-flat-fee" type="number" step="0.5" value={pricing.flat_fee_amount} onChange={(e) => setPricing({ ...pricing, flat_fee_amount: parseFloat(e.target.value || "0") })} data-testid="pricing-flat-fee" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pricing-min-cons">Min consumption per person (EUR)</Label>
              <Input id="pricing-min-cons" type="number" step="0.5" value={pricing.min_consumption_per_person} onChange={(e) => setPricing({ ...pricing, min_consumption_per_person: parseFloat(e.target.value || "0") })} data-testid="pricing-min-cons" />
            </div>
            <div className="space-y-2 flex flex-col justify-end">
              <label className="inline-flex items-center gap-2 text-sm">
                <Checkbox checked={!!pricing.reservation_only_enabled} onCheckedChange={(v) => setPricing({ ...pricing, reservation_only_enabled: Boolean(v) })} data-testid="pricing-reservation-enabled" />
                Reservation mode enabled
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Checkbox checked={!!pricing.pay_now_enabled} onCheckedChange={(v) => setPricing({ ...pricing, pay_now_enabled: Boolean(v) })} data-testid="pricing-pay-now-enabled" />
                Pay-now mode enabled (requires Stripe)
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Checkbox checked={!!pricing.allow_user_choice} onCheckedChange={(v) => setPricing({ ...pricing, allow_user_choice: Boolean(v) })} data-testid="pricing-allow-choice" />
                Let user choose
              </label>
              <label className="inline-flex items-center gap-2 text-sm">
                <Checkbox checked={!!pricing.deposit_required} onCheckedChange={(v) => setPricing({ ...pricing, deposit_required: Boolean(v) })} data-testid="pricing-deposit" />
                Deposit required (when pay-now)
              </label>
            </div>
          </div>
        </section>

        {/* DIAGNOSTICS — superadmin only on the backend; the panels show "—" if access is forbidden */}
        {(stripeDiag || emailDiag || webhookEvents) && (
          <section className="space-y-5" data-testid="diagnostics-section" aria-labelledby="diagnostics-heading">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-primary/80">— Operator</p>
                <h2 id="diagnostics-heading" className="font-display text-3xl mt-2">
                  Diagnostics
                </h2>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshDiagnostics}
                disabled={refreshingDiag}
                className="border-border"
                data-testid="diagnostics-refresh"
              >
                {refreshingDiag ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
                Refresh
              </Button>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              {stripeDiag && (
                <div className="border border-border rounded-xl bg-card p-5" data-testid="stripe-diagnostic-card">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-primary" />
                    <h3 className="font-display text-lg">Stripe</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <StatusPill ok={stripeDiag.stripe_key_present} label={`Key: ${stripeDiag.stripe_key_mode}`} testid="stripe-status-key" />
                    <StatusPill ok={stripeDiag.stripe_enabled} label="SDK ready" testid="stripe-status-sdk" />
                    <StatusPill ok={stripeDiag.webhook_secret_present} label="Webhook secret" testid="stripe-status-webhook-secret" />
                    {stripeDiag.test_payment_mock && (
                      <span className="text-xs px-2.5 py-1 rounded-full border border-amber-500/40 text-amber-300">
                        Mock mode ON
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Set <code className="text-foreground">STRIPE_WEBHOOK_SECRET</code> in backend .env to verify webhook signatures in production.
                  </p>
                </div>
              )}

              {emailDiag && (
                <div className="border border-border rounded-xl bg-card p-5" data-testid="email-diagnostic-card">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-primary" />
                    <h3 className="font-display text-lg">Email (SMTP)</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <StatusPill ok={emailDiag.smtp_host_present} label="Host" testid="email-status-host" />
                    <StatusPill ok={emailDiag.smtp_user_present} label="User" testid="email-status-user" />
                    <StatusPill ok={emailDiag.smtp_pass_present} label="Pass" testid="email-status-pass" />
                    <StatusPill ok={emailDiag.smtp_ready} label={emailDiag.smtp_ready ? "Ready" : "Fallback (file log)"} testid="email-status-ready" />
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    Falls back to <code className="text-foreground">/app/memory/last_email.txt</code> when SMTP not configured.
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Input
                      type="email"
                      placeholder="you@example.com"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      aria-label="Test email recipient"
                      data-testid="email-test-input"
                    />
                    <Button
                      type="button"
                      onClick={sendTestEmail}
                      disabled={sendingTest}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="email-test-send"
                    >
                      {sendingTest ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><Send className="w-4 h-4 mr-1.5" /> Send test</>)}
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {webhookEvents && (
              <div className="border border-border rounded-xl bg-card p-5" data-testid="webhook-events-card">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Webhook className="w-4 h-4 text-primary" />
                    <h3 className="font-display text-lg">Recent Stripe webhooks</h3>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {webhookEvents.total} total · capped at {webhookEvents.cap}
                  </span>
                </div>
                <div className="mt-4 -mx-5 px-5 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="webhook-events-table">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border/60">
                        <th className="py-2 pr-3">When</th>
                        <th className="py-2 pr-3">Event</th>
                        <th className="py-2 pr-3">Outcome</th>
                        <th className="py-2 pr-3">Sig</th>
                        <th className="py-2 pr-3">Booking</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!webhookEvents.items || webhookEvents.items.length === 0) && (
                        <tr><td colSpan={5} className="py-6 text-center text-muted-foreground">No webhook events yet.</td></tr>
                      )}
                      {(webhookEvents.items || []).map((ev) => (
                        <tr key={ev.id} className="border-b border-border/30" data-testid={`webhook-row-${ev.id}`}>
                          <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                            {new Date(ev.received_at).toLocaleString("en-GB", { hour12: false })}
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs">{ev.event_type}</td>
                          <td className="py-2 pr-3">{ev.outcome}</td>
                          <td className="py-2 pr-3">
                            {ev.signature_verified ? (
                              <span className="text-primary">✓</span>
                            ) : (
                              <span className="text-amber-400">—</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 font-mono text-xs">{ev.booking_code || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>
        )}
      </div>
    </AdminLayout>
  );
}
